import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./components/LoginPage";
import NewLoginPage from "./components/newloginpage/NewLoginPage";
import EmployeeDashboard from "./components/EmployeeDashboard";
import EmployeeLandingPage from "./components/admindashboard/EmployeeLandingPage";
import { ThemeProvider } from "./components/admindashboard/useTheme.js";
import { IntroProvider } from "./components/admindashboard/useIntro.js";
import AdminDashboard from "./components/AdminDashboard";
import NewAdminDashboard from "./components/admindashboard/NewAdminDashboard";
import ApplicationHub from "./components/HRIS/ApplicationHub";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Base URL self-healing (permanent fix for staging deployments going down).
//
// Problem: the build-time env var REACT_APP_BACKEND_URL was sometimes set to
// a staging URL (e.g. https://payora-hr--25gvxeb2.preview.emergentagent.com)
// that is unrelated to the actual backend serving the page. When that staging
// environment returns 503 Service Unavailable, users see "Backend unreachable"
// on the sign-in page and every authenticated call times out at 30 seconds.
//
// Fix: Prefer the same-origin `/api` prefix 99% of the time because in all
// real deployments (production + most local dev) the FastAPI backend is
// co-located at the same origin under the /api prefix (or reverse-proxied
// there by Cloudflare / nginx). Only fall back to the env-configured URL if
// the same-origin /health probe is known-unreachable and the env URL is set.
// This keeps existing cross-origin (frontend = *.vercel.app, backend =
// *.iaworkplace.com) deployments working, while fixing the accidental
// staging-url-in-prod anti-pattern seen in the 2026-09-30 screenshots.
// ---------------------------------------------------------------------------
function _resolveApiBase() {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return `${window.location.origin}/api`;
    }
  } catch (_) {}
  if (BACKEND_URL && typeof BACKEND_URL === 'string' && BACKEND_URL.trim()) {
    return `${BACKEND_URL.replace(/\/$/, '')}/api`;
  }
  return '/api';
}
const API = _resolveApiBase();

// Create axios instance with interceptors for token management.
// Default timeout 30s retained for file-uploads and slow POSTs. Latency-
// sensitive GETs (session refresh, attendance hydrate) override this to an
// aggressive 8s so transient backend hangs do not block the UI for a full
// half-minute — which was a major contributor to the "30s timeout of doom"
// symptom in the 2026-09-30 regression screenshots.
const axiosInstance = axios.create({
  baseURL: API,
  timeout: 30000,
});

const getStoredUser = () => {
  try {
    const rawUser = localStorage.getItem('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
};

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Basic CSRF mitigation for same-origin POST: derive a token from storage if present
  try {
    const csrf = localStorage.getItem('csrf_token');
    if (csrf && config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  } catch (_) { /* ignore */ }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children, requiredRole, user }) => {
  const effectiveUser = user || getStoredUser() || {};
  const token = localStorage.getItem('token');
  
  if (!token || !effectiveUser.email) {
    return <Navigate to="/login" />;
  }
  
  if (requiredRole && effectiveUser.role !== requiredRole) {
    return <Navigate to="/unauthorized" />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isSilenceableNetworkReason = (reason) => {
      if (!reason) return false;
      if (typeof reason === 'object') {
        const name = reason.name;
        const msg = reason.message ? String(reason.message) : '';
        if (name === 'AbortError' || name === 'NetworkError' || name === 'TimeoutError') return true;
        if (msg.includes('ERR_CONNECTION_RESET') || msg.includes('ERR_CONNECTION_REFUSED') ||
            msg.includes('ERR_NAME_NOT_RESOLVED') || msg.includes('ERR_INTERNET_DISCONNECTED') ||
            msg.includes('Failed to fetch')) return true;
      }
      if (typeof reason === 'string') {
        return reason.includes('ERR_CONNECTION_RESET') || reason.includes('ERR_CONNECTION_REFUSED') ||
               reason.includes('Failed to fetch') || reason.includes('AbortError');
      }
      return false;
    };

    const isSilenceableErrorEvent = (event) => {
      const msg = event.message ? String(event.message) : '';
      if (msg.includes('ERR_CONNECTION_RESET') || msg.includes('ERR_CONNECTION_REFUSED') ||
          msg.includes('Failed to load resource') || msg.includes('net::ERR_')) {
        return true;
      }
      if (msg.includes('the server responded with a status of ')) {
        const statusMatch = msg.match(/status of (\d+)/);
        if (statusMatch) {
          const code = Number(statusMatch[1]);
          if (!Number.isNaN(code) && (code === 404 || code === 405 || code === 502 || code === 520 || code === 524)) {
            return true;
          }
        }
      }
      return false;
    };

    const onUnhandledRejection = (event) => {
      if (isSilenceableNetworkReason(event.reason)) {
        try { event.preventDefault(); } catch (_) { /* noop */ }
        return;
      }
      const anyError = event.reason;
      if (anyError && anyError.config && anyError.isAxiosError) {
        const status = anyError.response?.status;
        if (status === 404 || status === 405 || status === 502 || status === 520 || status === 524) {
          try { event.preventDefault(); } catch (_) { /* noop */ }
          return;
        }
      }
    };

    const onError = (event) => {
      if (isSilenceableErrorEvent(event)) {
        try { event.preventDefault(); } catch (_) { /* noop */ }
      }
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection, true);
    window.addEventListener('error', onError, true);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
      window.removeEventListener('error', onError, true);
    };
  }, []);

  useEffect(() => {
    const hydrateUser = async () => {
      const token = localStorage.getItem('token');
      const storedUser = getStoredUser();

      try {
        if (!localStorage.getItem('csrf_token')) {
          const arr = new Uint8Array(24);
          (window.crypto || window.msCrypto).getRandomValues(arr);
          localStorage.setItem('csrf_token', Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join(''));
        }
      } catch (_) { /* ignore */ }

      if (!token || !storedUser) {
        setLoading(false);
        return;
      }

      setUser(storedUser);

      // Safety deadman: unconditionally clear loading after 40s so users can use
      // cached credentials even if origin TCP-connects but never responds.
      // NOTE: settled is ONLY used to prevent duplicate setLoading calls. It
      // MUST NOT gate whether setLoading(false) runs on the success/error path.
      let settled = false;
      const deadman = setTimeout(() => {
        if (!settled) {
          settled = true;
          setLoading(false);
        }
      }, 40000);

      try {
        // Aggressive 8 s timeout for the user-session refresh. 30 s default
        // was too long: a hung backend (or staging 503) would leave the page
        // in a loading tombstone state for a half-minute, forcing users to
        // hard-refresh. With 8 s we fall through to cached localStorage user
        // data quickly and surface the rest of the UI while the backend is
        // being restored.
        const response = await axiosInstance.get('/auth/me', { timeout: 8000 });
        localStorage.setItem('user', JSON.stringify(response.data));
        setUser(response.data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          console.error('Failed to refresh user session, using cached user data:', e);
        }
      } finally {
        clearTimeout(deadman);
        // Always unblock loading. The settled guard just avoids redundant
        // setState calls if the deadman already fired.
        if (!settled) {
          settled = true;
        }
        setLoading(false);
      }
    };

    hydrateUser();
  }, []);

  const handleLogin = useCallback((userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const landingRouteForUser = (userDoc) => {
    const role = userDoc?.role;
    if (role === 'employee') return '/employee';
    return '/admin/new';
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/application-hub" element={<ApplicationHub />} />
          <Route 
            path="/login" 
            element={
              user ? (
                <Navigate to={landingRouteForUser(user)} replace />
              ) : (
                <NewLoginPage onLogin={handleLogin} axiosInstance={axiosInstance} />
              )
            } 
          />
          <Route
            path="/login-classic"
            element={
              user ? (
                <Navigate to={landingRouteForUser(user)} replace />
              ) : (
                <LoginPage onLogin={handleLogin} axiosInstance={axiosInstance} />
              )
            }
          />
          <Route 
            path="/employee" 
            element={
              <ProtectedRoute requiredRole="employee" user={user}>
                <div className="new-admin-dash">
                  <ThemeProvider scopeClass="new-admin-dash">
                    <IntroProvider duration={2200}>
                      <EmployeeLandingPage user={user} onLogout={handleLogout} axiosInstance={axiosInstance} />
                    </IntroProvider>
                  </ThemeProvider>
                </div>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/employee/classic"
            element={
              <ProtectedRoute requiredRole="employee" user={user}>
                <EmployeeDashboard user={user} onLogout={handleLogout} axiosInstance={axiosInstance} />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <AdminDashboard user={user} onLogout={handleLogout} axiosInstance={axiosInstance} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/classic" 
            element={
              <ProtectedRoute requiredRole="admin" user={user}>
                <AdminDashboard user={user} onLogout={handleLogout} axiosInstance={axiosInstance} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/new" 
            element={
              <ProtectedRoute user={user}>
                <NewAdminDashboard user={user} onLogout={handleLogout} axiosInstance={axiosInstance} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/unauthorized" 
            element={
              <div className="min-h-screen flex items-center justify-center bg-red-50">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-red-600 mb-4">Unauthorized Access</h1>
                  <p className="text-gray-600 mb-4">You don&apos;t have permission to access this page.</p>
                  <button 
                    onClick={handleLogout}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                  >
                    Return to Login
                  </button>
                </div>
              </div>
            } 
          />
          <Route 
            path="/" 
            element={
              user ? (
                <Navigate to={landingRouteForUser(user)} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
